import { useMemo, useReducer } from 'react'
import type { Dispatch } from 'react'
import experienceOfferPageStateReducer, {
  INITIAL_EXPERIENCE_PAGE_STATE,
  type ExperienceOfferPageDispatchAction,
  type ExperienceOfferPageState,
} from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'

export interface TripExperienceInitialState {
  date?: string
  time?: string
  ticketCounts?: Record<string, number>
  redemptionLocationId?: string
  pickupPointId?: string
  ticketModesMap?: Record<string, string>
}

function buildInitialState(
  override?: TripExperienceInitialState,
  standaloneOffer?: App.ExperienceOffer,
): ExperienceOfferPageState {
  // Pre-select when there's only one option so the dates/times fetch fires on first render
  const singleLocation = standaloneOffer?.redemptionLocations.length === 1
    ? standaloneOffer.redemptionLocations[0]
    : undefined
  const singlePickup = standaloneOffer?.pickupPoints.length === 1
    ? standaloneOffer.pickupPoints[0]
    : undefined

  if (!override) {
    return {
      ...INITIAL_EXPERIENCE_PAGE_STATE,
      redemptionLocation: singleLocation,
      pickupPoint: singlePickup,
    }
  }
  return {
    ...INITIAL_EXPERIENCE_PAGE_STATE,
    date: override.date,
    time: override.time,
    ticketCounts: override.ticketCounts ?? {},
    redemptionLocation: standaloneOffer?.redemptionLocations?.find(l => l.id === override.redemptionLocationId) ?? singleLocation,
    pickupPoint: standaloneOffer?.pickupPoints?.find(p => p.id === override.pickupPointId) ?? singlePickup,
    ticketModesMap: override.ticketModesMap,
  }
}

type ReturnType = [ExperienceOfferPageState, Dispatch<ExperienceOfferPageDispatchAction>]

function useTripExperiencePageState(
  initialOverride?: TripExperienceInitialState,
  standaloneOffer?: App.ExperienceOffer,
): ReturnType {
  const [state, dispatch] = useReducer(
    experienceOfferPageStateReducer,
    INITIAL_EXPERIENCE_PAGE_STATE,
    (_base: ExperienceOfferPageState) => buildInitialState(initialOverride, standaloneOffer),
  )
  return useMemo(() => [state, dispatch] as ReturnType, [state, dispatch])
}

export default useTripExperiencePageState
